import { memo } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentPriceForTrade } from '@pages/Trade/Portfolio/hooks/useCurrentPricesStore'
import { formatSymbol } from '@pages/Trade/Portfolio/utils/formatters'
import { useTradingSessionStatus } from '@pages/Trade/Widget/hooks/useTradingSessionStatus'
import { getStepDecimals } from '@pages/Trade/utils/helpers'

import { Body1, Caption1 } from '@ui/Typography'
import { Button, IconButton } from '@ui/components/Button'

import type {
  DxTradeInstrumentWithTradingMeta,
  DxTradeRawCandle,
  InstrumentActionHandler,
  InstrumentActionType,
} from '@model/DxTrade.types'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box/Box'

import { Sparkline, SparklineSkeleton } from './components/Sparkline'
import { getFavoriteIcon } from './helpers'
import { useChangePercent } from './hooks/useChangePercent'

import styles from './Instruments.module.scss'

const PRICE_PLACEHOLDER = '—'

const SPARKLINE_WIDTH = 80
const SPARKLINE_HEIGHT = 24
const SPARKLINE_LINE_WIDTH = 1

export type InstrumentCardProps = {
  instrument: DxTradeInstrumentWithTradingMeta
  onAction?: InstrumentActionHandler
  latestDailyCandle?: DxTradeRawCandle
  sparklinePrices?: number[]
  isSparklineLoading?: boolean
  isFirstCard?: boolean
}
export const InstrumentCard = memo(
  ({
    instrument,
    onAction,
    isFavorite,
    onToggleFavorite,
    latestDailyCandle,
    sparklinePrices,
    isSparklineLoading,
    isFirstCard,
  }: InstrumentCardProps & {
    isFavorite?: boolean
    onToggleFavorite?: (symbol: string) => void
  }) => {
    const { t } = useTranslation()

    const description = instrument.description ?? ''
    const symbol = formatSymbol(instrument.symbol, description || undefined)

    const tradingHours = instrument.tradingHours ?? null
    const hasTradingHours = (tradingHours?.length ?? 0) > 0
    const { isOpen } = useTradingSessionStatus(tradingHours)
    const isMarketClosed = hasTradingHours && !isOpen

    const sellPrice = useCurrentPriceForTrade(instrument.symbol ?? null, 'SELL', 'order')
    const buyPrice = useCurrentPriceForTrade(instrument.symbol ?? null, 'BUY', 'order')

    const { formatNumber } = useFormatNumber()
    const fractionDigits = getStepDecimals(instrument.priceIncrement)
    const formatPrice = (p: number | null | undefined) =>
      p != null
        ? formatNumber(p, { minDecimalPlaces: fractionDigits, maxDecimalPlaces: fractionDigits })
        : PRICE_PLACEHOLDER

    const sellActionText = t('Instruments.Sell')
    const buyActionText = t('Instruments.Buy')
    const sellPriceText = formatPrice(sellPrice)
    const buyPriceText = formatPrice(buyPrice)

    const { label: changePercentLabel, color: changePercentColor } = useChangePercent(
      sellPrice,
      latestDailyCandle?.openingPrice
    )

    const handleAction = (action: InstrumentActionType) => {
      if (!instrument.symbol) {
        return
      }

      const instrumentForAction = instrument
      onAction?.(instrumentForAction, action)
    }

    const favoriteIcon = getFavoriteIcon(isFavorite)

    const handleFavoriteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      if (instrument.symbol && onToggleFavorite) {
        onToggleFavorite(instrument.symbol)
      }
    }

    return (
      <div
        className={styles.mobileCard}
        onClick={() => handleAction('marketWidget')}
        data-tour-target={isFirstCard ? 'trade.mobileChartTrigger' : undefined}
      >
        <div className={styles.mobileCardHeader}>
          <Box flex align='center' justify='space-between'>
            <div className={styles.mobileTitleBlock}>
              <Body1 medium>{symbol}</Body1>

              <IconButton.Ghost icon={favoriteIcon} onClick={handleFavoriteClick} />
            </div>

            {isMarketClosed ? (
              <Caption1 color='text-med-em'>{t('Instruments.Market closed')}</Caption1>
            ) : sparklinePrices ? (
              <Sparkline
                prices={sparklinePrices}
                width={SPARKLINE_WIDTH}
                height={SPARKLINE_HEIGHT}
                lineWidth={SPARKLINE_LINE_WIDTH}
              />
            ) : isSparklineLoading ? (
              <SparklineSkeleton
                width={SPARKLINE_WIDTH}
                height={SPARKLINE_HEIGHT}
                strokeWidth={SPARKLINE_LINE_WIDTH}
              />
            ) : null}
          </Box>
          <Box flex align='center' gap='x4' justify='space-between'>
            <Caption1 color='text-med-em'>{description}</Caption1>
            <Caption1
              medium
              color={changePercentLabel ? changePercentColor : 'text-low-em'}
              className={styles.priceValue}
            >
              {changePercentLabel ?? PRICE_PLACEHOLDER}
            </Caption1>
          </Box>
        </div>

        <div
          className={styles.mobileFooter}
          data-tour-target={isFirstCard ? 'trade.buySell' : undefined}
        >
          <Button.Danger
            fullWidth
            size='sm'
            textOverflow='truncate'
            tabularNums
            endDecorator={sellPriceText}
            onClick={(event) => {
              event.stopPropagation()
              handleAction('sell')
            }}
          >
            {sellActionText}
          </Button.Danger>
          <Button.Success
            fullWidth
            size='sm'
            textOverflow='truncate'
            tabularNums
            endDecorator={buyPriceText}
            onClick={(event) => {
              event.stopPropagation()
              handleAction('buy')
            }}
          >
            {buyActionText}
          </Button.Success>
        </div>
      </div>
    )
  }
)

InstrumentCard.displayName = 'InstrumentCard'
